import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { UserProfileService } from '../../settings/services/user-profile.service';
import { workspaceRepickUrl } from '../../setup/workspace-repick';
import { isEmbedded } from '../../shared/constants/embedded.regex';
import { SemerisRoutes } from '../../shared/constants/routes';
import { isOnboardingTenant } from '../../shared/models/account-status.model';
import { wsLink } from '../../shared/utils/workspace-link';

@Injectable({ providedIn: 'root' })
export class AssetGuard implements CanActivate {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly userProfileService = inject(UserProfileService);
  private readonly router = inject(Router);

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | boolean {
    return (
      isEmbedded() ||
      this.userProfileService.getFreshOrLoadedProfile().pipe(
        switchMap((user) => {
          if (user.blocked) {
            return of(this.router.createUrlTree(wsLink(SemerisRoutes.blocked)));
          }

          // First-time / force-pick users flow through the full-screen setup
          // wizard until they finish it. `/setup` has no AssetGuard, so this
          // can't loop; onboarding skips it, since `/setup` sends them back.
          if (!user.setupCompleted && !isOnboardingTenant(user.tenantShortName)) {
            return of(this.router.createUrlTree(wsLink(SemerisRoutes.setup)));
          }

          return this.store.select(AppSelectors.assets).pipe(
            // Waits for init: pre-init `availableWorkspaces` is [], which would make everyone
            // look unresolved.
            filter((assets) => !!assets),
            take(1),
            withLatestFrom(this.store.select(AppSelectors.selectedWorkspaceIsUnresolved)),
            map(([assets, workspaceIsUnresolved]) => {
              const hasAccess = !!assets?.length;

              if (!hasAccess) {
                console.log('No access to assets');
                return this.router.createUrlTree(wsLink(SemerisRoutes.welcome));
              }

              // Never while impersonating: the workspace is unresolved by design there, and
              // re-picking would overwrite the admin's own.
              if (!workspaceIsUnresolved || user.impersonatedTenant) {
                return true;
              }

              return workspaceRepickUrl(this.router);
            }),
          );
        }),
        catchError(() => of(false)),
      )
    );
  }
}
